import { useExperimentalQuery } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalQuery.ts';
import { MediaType } from '@confluence/graphql/entry-points/MediaType.ts';

import type {
	SpaceUnlicensedPermissionQuery as SpaceUnlicensedPermissionQueryData,
	SpaceUnlicensedPermissionQueryVariables,
} from './__types__/SpaceUnlicensedPermissionQuery.ts';

/**
 * This query is only used for checking unlicensed permissions.
 * Refer to other GraphQL queries if checking other space properties or permissions.
 *
 * The unlicensed permission object is the object with an undefined subject
 * in the `permissions` array in `results` in the response.
 */
export const useExperimentalUnlicensedPermissionQuery = ({
	variables,
}: {
	variables: SpaceUnlicensedPermissionQueryVariables;
}) => {
	return useExperimentalQuery<
		SpaceUnlicensedPermissionQueryData,
		SpaceUnlicensedPermissionQueryVariables,
		SpaceResponse
	>({
		url: `/wiki/rest/api/space?spaceKey=${variables.spaceKey}&expand=permissions`,
		method: 'GET',
		headers: {
			'Content-Type': MediaType.JSON,
		},
		parseResult: (payload: SpaceResponse, _variables: SpaceUnlicensedPermissionQueryVariables) => {
			const unlicensedAccessPerm = (payload.results?.[0]?.permissions || []).find(
				(perm: Permission) =>
					perm.unlicensedAccess &&
					perm.operation?.operation === 'read' &&
					perm.operation?.targetType === 'space',
			);
			return {
				experimentalUnlicensedPermission: {
					unlicensedPermissionId: unlicensedAccessPerm?.id ?? null,
				},
			};
		},
		parseResponseOnError: () => {
			return {
				results: [],
			};
		},
		variables,
	});
};

interface Permission {
	id?: number;
	unlicensedAccess?: boolean;
	operation?: {
		operation?: string;
		targetType?: string;
	};
}

interface SpaceResponse {
	results?: Array<{
		permissions?: Permission[];
	}>;
}
